import React, { useEffect } from "react";
import type { RouteComponentProps } from "@gatsbyjs/reach-router";
import { z } from "zod";
import { Controller, useForm } from "react-hook-form";
import {
  getCardNumberMaxLengthByState,
  getCardNumberTypeByState,
  verifyAustralianLicenceNumber,
} from "utilities/helpers";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Typography,
  Form,
  Input,
  NativeSelect,
  DateInput,
  Checkbox,
  Button,
  PageLoader,
  Alert,
  toast,
} from "@picklebet/ui";
import { Link } from "@/features/shell/components/shared/Link/Link";
import { navigate } from "utilities/navigate";
import { userVerificationSchema } from "./schemas/userVerificationSchema";
import { useProfile } from "@/hooks/firestore/user/useProfile";
import { verificationService } from "features/account/services/verification";
import { useVerificationFormAttempt } from "features/account/hooks/useVerifications";
import { useCustomerLicence } from "@/features/auth/hooks/usePermission";
import { handleFailedVerificationAttempt } from "features/account/utils/verification";
import { telemetry } from "@/utilities/telemetry";
import { DVSConsent } from "@/features/account/components/verification/DVSConsent";
import { ALPHANUMERIC } from "utilities/regex";
import { VERIFICATION_DATA_SOURCES } from "features/account/types/Verification";
import { isValidFutureDate } from "utilities/datetime";

import * as styles from "./AustralianDriverLicense.module.scss";

const schema = userVerificationSchema
  .extend({
    state: z.string().min(1, "State is required"),
    number: z
      .string()
      .min(1, "A valid driver's licence number is required")
      .regex(ALPHANUMERIC, "Only alphanumeric characters are allowed"),
    cardNumber: z.string().min(1, "A valid card number is required"),
    expiryDate: z
      .string({ error: "A valid expiry date is required" })
      .refine(isValidFutureDate, {
        message: "Your expiry date is invalid or licence has expired",
      }),
  })
  .superRefine((data, ctx) => {
    if (data.cardNumber && data.state) {
      const validation = verifyAustralianLicenceNumber(
        data.state,
        data.cardNumber,
      );
      if (typeof validation === "string") {
        ctx.addIssue({
          code: "custom",
          message: validation,
          path: ["cardNumber"],
        });
      }
    }
  });

type FormValues = z.infer<typeof schema>;

type AustralianDriverLicenceProps = {
  prefix?: string;
} & RouteComponentProps;

const AustralianDriverLicence = ({
  prefix = "/account",
}: AustralianDriverLicenceProps) => {
  const licence = useCustomerLicence();
  const { attempt, isLoading, hasSubmitted, markSubmitted } =
    useVerificationFormAttempt();

  const {
    control,
    register,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting },
    reset,
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
  });

  const { profile } = useProfile();

  useEffect(() => {
    if (profile) {
      reset({
        firstName: profile.firstName,
        lastName: profile.lastName,
        dateOfBirth: profile.dateOfBirth,
        state: profile.state ?? "",
      });
    }
  }, [profile]);

  const consent = watch("consent", false);
  const currentState = watch("state", "");

  const onSubmit = async ({ consent, ...fields }: FormValues) => {
    if (!attempt) {
      toast({
        variant: "danger",
        description:
          "Something went wrong. Please refresh and try again or contact support.",
      });
      return;
    }

    const verificationType =
      VERIFICATION_DATA_SOURCES.AUSTRALIAN_DRIVERS_LICENCE;

    const result = await verificationService.submitAttemptData({
      attemptId: attempt.attemptId,
      attemptType: "VIXIdentityDocuments",
      type: "AustralianDriversLicence",
      terms: consent,
      fields,
    });

    if (!result.ok) {
      handleFailedVerificationAttempt(verificationType, licence);
      return;
    }

    telemetry.submittedVerificationForm({
      outcome: "success",
      type: verificationType,
      licence: licence ?? undefined,
    });

    markSubmitted();
    navigate(`${prefix}/verification`);
  };

  if (isLoading || hasSubmitted) return <PageLoader />;

  if (!attempt && !isSubmitting) {
    return (
      <Alert
        color="danger"
        actions={
          <Button
            onClick={() => window.location.reload()}
            size="md"
            variant="danger"
          >
            Refresh
          </Button>
        }
      >
        Unable to load verification option, please refresh to try again.
      </Alert>
    );
  }

  return (
    <>
      <Form
        onSubmit={handleSubmit(onSubmit)}
        fixedWidth
        disabled={isSubmitting}
      >
        <Typography variant={`h4`} noSpacing>
          Australian Drivers Licence
        </Typography>
        <Typography variant={`body`}>
          Please enter your identity information, exactly as it appears on your
          Australian Drivers Licence.
        </Typography>
        <Typography variant={`body`}>
          Don't have a Drivers Licence? Select from a list of{" "}
          <Link to={`${prefix}/verification`}>Verification options</Link>.
        </Typography>
        <Input
          {...register("number")}
          label={`Licence Number`}
          errorMessage={errors?.number?.message}
          maxLength={10}
        />

        <Input
          {...register("cardNumber")}
          label={
            <>
              <div>Card Number</div>{" "}
              <div className={styles.cardNumber}>
                Click{" "}
                <Link
                  to="https://www.usi.gov.au/about/forms-id/drivers-licence"
                  target="_blank"
                >
                  here
                </Link>{" "}
                for help identifying your card number
              </div>
            </>
          }
          errorMessage={errors?.cardNumber?.message}
          maxLength={getCardNumberMaxLengthByState(currentState)}
          inputMode={
            getCardNumberTypeByState(currentState) === "numeric"
              ? "numeric"
              : "text"
          }
        />
        <Controller
          control={control}
          name={"expiryDate"}
          render={({ field: { ref: _, ...field } }) => (
            <DateInput
              id={"expiryDate"}
              {...field}
              errorMessage={errors.expiryDate?.message}
              label={`Expiry Date`}
            />
          )}
        />
        <NativeSelect
          options={[
            { value: "ACT", label: "Australian Capital Territory" },
            { value: "NSW", label: "New South Wales" },
            { value: "NT", label: "Northern Territory" },
            { value: "QLD", label: "Queensland" },
            { value: "SA", label: "South Australia" },
            { value: "TAS", label: "Tasmania" },
            { value: "VIC", label: "Victoria" },
            { value: "WA", label: "Western Australia" },
          ]}
          label={`State`}
          {...register("state")}
          errorMessage={errors?.state?.message}
        />
        <Input
          {...register("firstName")}
          label={`First Name`}
          errorMessage={errors?.firstName?.message}
        />
        <Input
          {...register("lastName")}
          label={`Last Name`}
          errorMessage={errors?.lastName?.message}
        />
        <Controller
          control={control}
          name={"dateOfBirth"}
          render={({ field: { ref: _, ...field } }) => (
            <DateInput
              id={"dateOfBirth"}
              label="Date of Birth"
              {...field}
              errorMessage={errors.dateOfBirth?.message}
              hideLabel
            />
          )}
        />
        <Controller
          control={control}
          render={({ field: { onChange, value: _, ...field } }) => (
            <Checkbox
              id={`consent`}
              label={DVSConsent}
              checked={consent}
              onCheckedChange={(checked) => onChange?.(checked === true)}
              hasError={!!errors?.consent?.message}
              {...field}
            />
          )}
          name={`consent`}
        />
        <Button type={`submit`} loading={isSubmitting}>
          Verify Licence
        </Button>
      </Form>
    </>
  );
};

export { AustralianDriverLicence };
